import { Link } from "react-router-dom";
import type { GossipArticle } from "@/types/gossip";
import { formatCount } from "@/utils/format";

interface ArticleCardProps {
  article: GossipArticle;
  variant?: "grid" | "wide" | "compact";
  showRank?: number;
}

export default function ArticleCard({ article, variant = "grid", showRank }: ArticleCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex items-start gap-3 py-3"
      >
        {showRank !== undefined && (
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-background-200 font-label text-xs font-bold text-foreground-700 group-hover:bg-primary-500 group-hover:text-background-50">
            {showRank}
          </span>
        )}
        <span className="min-w-0">
          <span className="block text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
            {article.title}
          </span>
          <span className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
            <span>{article.categoryName}</span>
            <span className="text-background-400">·</span>
            <span>{article.relativeTime}</span>
          </span>
        </span>
      </Link>
    );
  }

  if (variant === "wide") {
    return (
      <article className="group grid gap-4 overflow-hidden rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 md:grid-cols-[220px_1fr]">
        <Link to={`/article/${article.slug}`} className="block h-40 w-full overflow-hidden rounded-md md:h-full md:min-h-[150px]">
          <img
            src={article.cover}
            alt={`${article.title} - 瓜料动态${article.categoryName}`}
            title={`${article.title} - 瓜料动态最新进展`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="flex flex-col py-1 pr-1">
          <div className="flex items-center gap-2 text-xs">
            <span className="rounded-full bg-primary-100 px-2.5 py-1 font-label text-primary-700">{article.categoryName}</span>
            <span className="text-foreground-500">{article.relativeTime}</span>
          </div>
          <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950">
            <Link to={`/article/${article.slug}`} className="transition-colors hover:text-primary-600">
              {article.title}
            </Link>
          </h3>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{article.summary}</p>
          <div className="mt-auto flex items-center gap-4 pt-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" /> {formatCount(article.readCount)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line" /> 热度 {article.hotScore}
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/article/${article.slug}`} className="block h-44 w-full overflow-hidden">
        <img
          src={article.cover}
          alt={`${article.title} - 瓜料动态${article.categoryName}`}
          title={`${article.title} - 瓜料动态最新进展`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center gap-2 text-xs">
          <span className="rounded-full bg-secondary-100 px-2.5 py-1 font-label text-secondary-900">{article.categoryName}</span>
          <span className="text-foreground-500">{article.relativeTime}</span>
        </div>
        <h3 className="mt-3 font-heading text-base font-bold leading-snug text-foreground-950">
          <Link to={`/article/${article.slug}`} className="transition-colors hover:text-primary-600">
            {article.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-600">{article.summary}</p>
        <div className="mt-auto flex items-center justify-between pt-4 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line" /> {formatCount(article.readCount)}
          </span>
          <span className="flex items-center gap-1 text-primary-600">
            查看详情 <i className="ri-arrow-right-line" />
          </span>
        </div>
      </div>
    </article>
  );
}